import {
  arrayUnion,
  doc,
  increment,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc
} from "firebase/firestore";
import { db } from "../firebase";
import { getLocalDateStr, isWithinPowerHour } from "./time";

const fallbackActionId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const createActionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return fallbackActionId();
};

const buildClarification = ({ actionId, message, authorRole, authorName, authorId }) => ({
  id: actionId || createActionId(),
  message: (message || "").trim(),
  authorRole: authorRole || "parent",
  authorName: authorName || null,
  authorId: authorId || null,
  createdAt: new Date()
});

export const executeAction = async (action) => {
  switch (action?.type) {
    case "task.submit":
      return submitTaskAction(action);
    case "task.claim":
      return claimTaskAction(action);
    case "task.approve":
      return approveTaskAction(action);
    case "task.fail":
      return failTaskAction(action);
    case "task.clarify":
      return requestClarificationAction(action);
    case "reward.request":
      return requestRewardAction(action);
    case "redemption.approve":
      return approveRedemptionAction(action);
    case "redemption.deny":
      return denyRedemptionAction(action);
    default:
      throw new Error(`Unknown action type: ${action?.type || "missing"}`);
  }
};

export const submitTaskAction = async ({ familyId, taskId, kidName }) => {
  if (!familyId || !taskId) return false;
  const taskRef = doc(db, "families", familyId, "tasks", taskId);
  await updateDoc(taskRef, {
    status: "pending",
    kidName: kidName || "Someone",
    needsClarification: false
  });
  return true;
};

export const claimTaskAction = async ({ familyId, taskId, kidId, kidName }) => {
  if (!familyId || !taskId || !kidId) return false;
  const taskRef = doc(db, "families", familyId, "tasks", taskId);
  await updateDoc(taskRef, {
    assignedTo: kidId,
    assignedToName: kidName || ""
  });
  return true;
};

export const requestRewardAction = async ({
  familyId,
  actionId,
  kidId,
  kidName,
  rewardId,
  rewardName,
  cost
}) => {
  if (!familyId || !actionId) return false;
  const redemptionRef = doc(db, "families", familyId, "redemptions", actionId);
  await setDoc(
    redemptionRef,
    {
      actionId,
      kidId: kidId || null,
      kidName: kidName || "Kid",
      rewardId: rewardId || null,
      rewardName: rewardName || "Reward",
      cost: Number(cost) || 0,
      status: "pending",
      createdAt: serverTimestamp()
    },
    { merge: true }
  );
  return true;
};

export const approveTaskAction = async ({ familyId, taskId, actionId }) => {
  if (!familyId || !taskId || !actionId) return false;
  const familyRef = doc(db, "families", familyId);
  const taskRef = doc(db, "families", familyId, "tasks", taskId);
  const historyRef = doc(db, "families", familyId, "history", actionId);

  const didApprove = await runTransaction(db, async (tx) => {
    const taskSnap = await tx.get(taskRef);
    if (!taskSnap.exists()) return false;
    const taskData = taskSnap.data() || {};
    if (taskData.status !== "pending") return false;

    const familySnap = await tx.get(familyRef);
    const familyData = familySnap.exists() ? familySnap.data() || {} : {};

    const kidRef = taskData.assignedTo
      ? doc(db, "families", familyId, "kids", taskData.assignedTo)
      : null;
    let kidData;
    if (kidRef) {
      const kidSnap = await tx.get(kidRef);
      if (kidSnap.exists()) kidData = kidSnap.data();
    }

    const now = new Date();
    const todayStr = getLocalDateStr(now);
    const yesterdayStr = getLocalDateStr(new Date(now.getTime() - 24 * 60 * 60 * 1000));
    const powerHourActive = Boolean(familyData?.powerHourEnabled) &&
      isWithinPowerHour(familyData?.powerHourStart, familyData?.powerHourEnd, now);
    const multiplier = powerHourActive
      ? Number(familyData?.powerHourMultiplier || 1.5)
      : 1;
    const basePoints = Number(taskData.points || 0);

    if (kidData && kidRef) {
      const awardPoints = Math.round(basePoints * multiplier);
      const lastApproved = kidData.lastApprovedDate;
      let nextStreak = 1;
      if (lastApproved === todayStr) {
        nextStreak = kidData.streakCount || 1;
      } else if (lastApproved === yesterdayStr) {
        nextStreak = (kidData.streakCount || 0) + 1;
      }
      const badges = Array.isArray(kidData.badges) ? [...kidData.badges] : [];
      if (nextStreak >= 7 && !badges.includes("7-day streak")) badges.push("7-day streak");
      if (nextStreak >= 30 && !badges.includes("30-day streak")) badges.push("30-day streak");

      tx.update(kidRef, {
        points: increment(awardPoints),
        streakCount: nextStreak,
        lastApprovedDate: todayStr,
        badges
      });
    }

    const kidName = kidData?.name || taskData.kidName || "Unknown";
    const pointsAwarded = kidData ? Math.round(basePoints * multiplier) : basePoints;

    tx.update(taskRef, {
      status: "completed",
      approvedAt: serverTimestamp(),
      needsClarification: false,
      clarifications: [],
      lastClarificationAt: null
    });
    tx.set(historyRef, {
      actionId,
      type: "approve",
      title: taskData.title,
      kidName,
      points: basePoints,
      pointsAwarded,
      createdAt: serverTimestamp()
    });

    return true;
  });

  return didApprove;
};

export const failTaskAction = async ({ familyId, taskId, actionId, message }) => {
  if (!familyId || !taskId || !actionId) return false;
  const taskRef = doc(db, "families", familyId, "tasks", taskId);
  const historyRef = doc(db, "families", familyId, "history", actionId);

  const didFail = await runTransaction(db, async (tx) => {
    const taskSnap = await tx.get(taskRef);
    if (!taskSnap.exists()) return false;
    const taskData = taskSnap.data() || {};
    if (taskData.status !== "pending") return false;
    const basePoints = Number(taskData.points || 0);
    const halvedPoints = basePoints > 0 ? Math.max(1, Math.round(basePoints / 2)) : 0;
    const kidRef = taskData.assignedTo
      ? doc(db, "families", familyId, "kids", taskData.assignedTo)
      : null;

    const updates = {
      status: "open",
      needsClarification: true,
      points: halvedPoints
    };
    if (message && message.trim()) {
      updates.clarifications = arrayUnion(
        buildClarification({
          actionId: `${actionId}-clarify`,
          message,
          authorRole: "parent",
          authorName: taskData.parentName || "Parent"
        })
      );
      updates.lastClarificationAt = serverTimestamp();
    }

    if (kidRef) {
      tx.update(kidRef, {
        streakCount: 0,
        lastApprovedDate: null
      });
    }

    tx.update(taskRef, updates);
    tx.set(historyRef, {
      actionId,
      type: "fail",
      title: taskData.title,
      kidName: taskData.assignedToName || taskData.kidName || "Unknown",
      pointsBefore: basePoints,
      pointsAfter: halvedPoints,
      note: message ? message.trim() : null,
      createdAt: serverTimestamp()
    });
    return true;
  });

  return didFail;
};

export const requestClarificationAction = async ({
  familyId,
  taskId,
  actionId,
  message,
  authorRole,
  authorName,
  authorId
}) => {
  if (!familyId || !taskId || !actionId || !message?.trim()) return false;
  const taskRef = doc(db, "families", familyId, "tasks", taskId);
  const historyRef = doc(db, "families", familyId, "history", actionId);

  const didUpdate = await runTransaction(db, async (tx) => {
    const taskSnap = await tx.get(taskRef);
    if (!taskSnap.exists()) return false;
    const taskData = taskSnap.data() || {};
    if (taskData.status === "completed" || taskData.status === "closed") return false;

    tx.update(taskRef, {
      status: "open",
      needsClarification: true,
      clarifications: arrayUnion(
        buildClarification({ actionId, message, authorRole, authorName, authorId })
      ),
      lastClarificationAt: serverTimestamp()
    });
    tx.set(historyRef, {
      actionId,
      type: "clarify",
      title: taskData.title,
      kidName: taskData.assignedToName || taskData.kidName || "Unknown",
      authorRole: authorRole || "parent",
      note: message.trim(),
      createdAt: serverTimestamp()
    });
    return true;
  });

  return didUpdate;
};

export const approveRedemptionAction = async ({ familyId, redemptionId, actionId }) => {
  if (!familyId || !redemptionId || !actionId) return false;
  const redemptionRef = doc(db, "families", familyId, "redemptions", redemptionId);
  const historyRef = doc(db, "families", familyId, "history", actionId);

  const didApprove = await runTransaction(db, async (tx) => {
    const redemptionSnap = await tx.get(redemptionRef);
    if (!redemptionSnap.exists()) return false;
    const redemptionData = redemptionSnap.data() || {};
    if (redemptionData.status !== "pending") return false;

    const kidRef = redemptionData.kidId
      ? doc(db, "families", familyId, "kids", redemptionData.kidId)
      : null;
    const cost = Number(redemptionData.cost || 0);

    if (kidRef) {
      tx.update(kidRef, { points: increment(-cost) });
    }
    tx.update(redemptionRef, { status: "approved", approvedAt: serverTimestamp() });
    tx.set(historyRef, {
      actionId,
      type: "reward",
      title: redemptionData.rewardName,
      kidName: redemptionData.kidName || "Unknown",
      points: cost,
      createdAt: serverTimestamp()
    });
    return true;
  });

  return didApprove;
};

export const denyRedemptionAction = async ({ familyId, redemptionId, actionId }) => {
  if (!familyId || !redemptionId || !actionId) return false;
  const redemptionRef = doc(db, "families", familyId, "redemptions", redemptionId);

  const didDeny = await runTransaction(db, async (tx) => {
    const redemptionSnap = await tx.get(redemptionRef);
    if (!redemptionSnap.exists()) return false;
    const redemptionData = redemptionSnap.data() || {};
    if (redemptionData.status !== "pending") return false;
    tx.update(redemptionRef, { status: "denied", approvedAt: serverTimestamp() });
    return true;
  });

  return didDeny;
};
